import React, { useState, useEffect } from 'react';
import { Input, List, Button, Spin, Empty, Pagination } from 'antd';
import axios from 'axios';
import { Page, UserSummary } from './data/types';
import { SearchOutlined } from '@ant-design/icons';

interface UserSearchSelectProps {
    onlyNarrators?: boolean;
    onSelect: (user: UserSummary) => void;
}

export const UserSearchSelect: React.FC<UserSearchSelectProps> = ({ onlyNarrators = false, onSelect }) => {
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize] = useState(5); // Compact page size for mobile screens
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Page<UserSummary> | null>(null);

    const fetchUsers = React.useCallback((searchVal: string, pageNum: number) => {
        setLoading(true);
        axios.get<Page<UserSummary>>(`/api/admin/users`, {
            params: {
                filter: searchVal,
                onlyNarrators: onlyNarrators,
                page: pageNum,
                size: pageSize
            }
        })
        .then(resp => {
            setData(resp.data);
        })
        .catch(err => {
            console.error("Error fetching users for lookup", err);
        })
        .finally(() => {
            setLoading(false);
        });
    }, [onlyNarrators, pageSize]);

    useEffect(() => {
        fetchUsers(filter, page);
    }, [filter, page, fetchUsers]);

    const handleSearch = (val: string) => {
        setFilter(val);
        setPage(0);
    };

    return (
        <div style={{ width: '100%' }}>
            <Input.Search
                placeholder="Search by name or email..."
                onSearch={handleSearch}
                enterButton={<SearchOutlined />}
                loading={loading}
                style={{ marginBottom: '16px' }}
                allowClear
            />
            {loading && !data ? (
                <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>
            ) : !data || data.content.length === 0 ? (
                <Empty description="No users found" />
            ) : (
                <div>
                    <List
                        size="small"
                        bordered
                        dataSource={data.content}
                        renderItem={user => (
                            <List.Item
                                actions={[
                                    <Button type="link" size="small" onClick={() => onSelect(user)}>
                                        Select
                                    </Button>
                                ]}
                            >
                                <List.Item.Meta
                                    title={user.name || user.nickname || "No Name"}
                                    description={user.email}
                                />
                            </List.Item>
                        )}
                    />
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Pagination
                            simple
                            size="small"
                            current={page + 1}
                            pageSize={pageSize}
                            total={data.totalElements}
                            onChange={(p) => setPage(p - 1)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
